import { z } from 'zod';
import { ProjectStatuses, Scenarios } from './enums';
import { dateSchema } from './fields';
import { idSchema } from './ids';
import { SORT_ORDERS } from './list';
import { OVERVIEW_SORTS, ROADMAP_FIELDS, ROADMAP_UNITS } from './overview';
import { yearMonthTextSchema } from './period';
import { TASK_STATUS_FILTERS } from './tasks';

/**
 * Per-user, per-workspace UI preferences (FR-DASH-005, FR-TASK-014). Each key has its own
 * schema; the API stores only values that validate against it.
 */
export const PREFERENCE_SCHEMAS = {
  /** Checked statuses of the dashboard filter; [] = none checked (= all shown). */
  dashboardStatuses: z.array(ProjectStatuses.schema),
  /** Filters of the tasks page. */
  tasksFilters: z.object({
    from: dateSchema.nullable(),
    to: dateSchema.nullable(),
    status: z.enum(TASK_STATUS_FILTERS),
    assigneeId: idSchema.nullable(),
  }),
  /** Overview filters (FR-OVR-007). */
  overviewFilters: z.object({
    from: dateSchema,
    to: dateSchema,
    statuses: z.array(ProjectStatuses.schema),
    sort: z.enum(OVERVIEW_SORTS),
    order: z.enum(SORT_ORDERS),
  }),
  /** Roadmap filters (FR-OVR-017). */
  roadmapFilters: z.object({
    from: yearMonthTextSchema,
    to: yearMonthTextSchema,
    statuses: z.array(ProjectStatuses.schema),
    view: Scenarios.schema,
    field: z.enum(ROADMAP_FIELDS),
    unit: z.enum(ROADMAP_UNITS),
    rate: z.number().positive().max(10_000),
  }),
} as const;

export type PreferenceKey = keyof typeof PREFERENCE_SCHEMAS;
export type PreferenceValue<K extends PreferenceKey> = z.infer<(typeof PREFERENCE_SCHEMAS)[K]>;
export const PREFERENCE_KEYS = Object.keys(PREFERENCE_SCHEMAS) as PreferenceKey[];
export const preferenceKeySchema = z.enum(PREFERENCE_KEYS as [PreferenceKey, ...PreferenceKey[]]);

export const preferenceSchema = z.object({ value: z.unknown() });
